/**
 * JSON Tree View Component
 * 
 * Interactive tree view for JSON data with expand/collapse,
 * search, and colored data types.
 */

import { ChevronsDown, ChevronsUp,ClipboardCopy, Settings, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef,useState } from 'react';

import { SearchBar } from '@/components/common/SearchBar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { copyToClipboard } from '@/lib/utils';

type JsonSearchMode = 'filter' | 'highlight';
export const DEFAULT_SEARCH_MODE: JsonSearchMode = 'highlight';

interface JsonTreeViewProps {
  data: unknown;
  initialExpanded?: boolean;
  maxDepth?: number;
  showTypes?: boolean;
  onShowTypesChange?: (showTypes: boolean) => void;
  zeroIndexed?: boolean;
  searchTerm?: string;
  onSearchTermChange?: (term: string) => void;
  searchMode?: JsonSearchMode;
  onSearchModeChange?: (mode: JsonSearchMode) => void;
  // Performance settings
  chunkSize?: number;
  maxTraversalNodes?: number;
  // Inline editing
  editMode?: boolean;
  onValueEdit?: (path: string, newValue: unknown) => void;
  /** Called when a new property/item is added in edit mode.
   *  kind='property': adds `"key": ""` to the object at path.
   *  kind='item': appends `""` to the array at path. */
  onNodeAdd?: (path: string, kind: 'property' | 'item', key?: string) => void;
  /** Called when a property/item is deleted in edit mode. */
  onNodeDelete?: (path: string) => void;
  /** Incrementing timestamp triggers expand-all from parent. */
  expandAllTrigger?: number;
  /** Incrementing timestamp triggers collapse-all from parent. */
  collapseAllTrigger?: number;
}

interface TreeNodeProps {
  keyName: string | number | null;
  value: unknown;
  index?: number;
  depth: number;
  maxDepth: number;
  path: string;
  expandedPaths: Set<string>;
  toggleExpand: (path: string) => void;
  searchTerm: string;
  searchMode: JsonSearchMode;
  showTypes: boolean;
  zeroIndexed: boolean;
  chunkSize: number;
  editMode: boolean;
  activeMatchPath: string | null;
  onValueEdit?: (path: string, newValue: unknown) => void;
  onNodeAdd?: (path: string, kind: 'property' | 'item', key?: string) => void;
  onNodeDelete?: (path: string) => void;
}

type ValueType = 'string' | 'number' | 'boolean' | 'null' | 'undefined' | 'array' | 'object';

const formatValueForCopy = (value: unknown): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const getValueType = (value: unknown): ValueType => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return 'array';
  return typeof value as ValueType;
};

const TYPE_COLORS: Record<ValueType, string> = {
  string: 'text-type-string',
  number: 'text-type-number',
  boolean: 'text-type-boolean',
  null: 'text-type-null',
  undefined: 'text-type-null',
  array: 'text-type-collection',
  object: 'text-type-collection',
};

const TYPE_LABELS: Record<ValueType, string> = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
  null: 'null',
  undefined: 'undefined',
  array: 'array',
  object: 'object',
};

const getValuePreview = (value: unknown, type: ValueType): string => {
  switch (type) {
    case 'array':
      return `Array[${(value as unknown[]).length}]`;
    case 'object':
      return `Object{${Object.keys(value as object).length}}`;
    case 'string':
      return `"${value}"`;
    case 'null':
      return 'null';
    case 'undefined':
      return 'undefined';
    case 'boolean':
      return String(value);
    default:
      return String(value);
  }
};

const highlightMatch = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm) return text;
  
  const lowerText = text.toLowerCase();
  const lowerSearch = searchTerm.toLowerCase();
  const index = lowerText.indexOf(lowerSearch);
  
  if (index === -1) return text;
  
  return (
    <>
      {text.substring(0, index)}
      <span className="bg-yellow-500/50 rounded px-0.5" data-json-search-hit="true">
        {text.substring(index, index + searchTerm.length)}
      </span>
      {text.substring(index + searchTerm.length)}
    </>
  );
};

// Results from search traversal
export interface SearchResult {
  matchingPaths: Set<string>;   // Paths that match or have matching descendants
  expandedPaths: Set<string>;   // Paths that should be expanded
  matchingLeafPaths: string[];  // Ordered list of directly-matching node paths (for stepping)
}

// Single-pass search traversal to pre-calculate all match information efficiently
export const performSearch = (
  data: unknown, 
  searchTerm: string, 
  maxNodes: number
): SearchResult => {
  const result: SearchResult = {
    matchingPaths: new Set<string>(),
    expandedPaths: new Set<string>(),
    matchingLeafPaths: [],
  };

  if (!searchTerm) return result;

  const lowerSearch = searchTerm.toLowerCase();
  const stack: Array<{ v: unknown; p: string; k: string | number | null }> = [
    { v: data, p: 'root', k: null }
  ];
  let nodesCount = 0;

  while (stack.length > 0 && nodesCount < maxNodes) {
    const { v, p, k } = stack.pop()!;
    nodesCount++;

    // Check if THIS node matches
    let isMatch = false;
    if (k !== null && String(k).toLowerCase().includes(lowerSearch)) isMatch = true;
    
    if (!isMatch) {
      const type = getValueType(v);
      if (type === 'string' || type === 'number' || type === 'boolean') {
        if (String(v).toLowerCase().includes(lowerSearch)) isMatch = true;
      } else if (type === 'null' || type === 'undefined') {
        if (type.includes(lowerSearch)) isMatch = true;
      }
    }

    if (isMatch) {
      // Track this as a directly-matching leaf for step-through navigation
      result.matchingLeafPaths.push(p);
      // If this matches, all its ancestors must be marked as matching and expanded
      result.matchingPaths.add(p);
      
      const parts = p.split('.');
      for (let i = 1; i < parts.length; i++) {
        const ancestorPath = parts.slice(0, i).join('.');
        result.matchingPaths.add(ancestorPath);
        result.expandedPaths.add(ancestorPath);
      }
    }

    // Still need to check children even if this node doesn't match
    const type = getValueType(v);
    if (type === 'object' || type === 'array') {
      const entries = type === 'array'
        ? (v as unknown[]).map((child, i) => [i, child] as [number, unknown])
        : Object.entries(v as object);

      const limit = Math.min(entries.length, 100); 
      for (let i = limit - 1; i >= 0; i--) {
        const [childK, childV] = entries[i];
        stack.push({ 
          v: childV, 
          p: `${p}.${childK}`, 
          k: childK 
        });
      }
    }
  }
  
  return result;
};

// Iterative path collection with safety limit
const collectAllPaths = (value: unknown, maxNodes: number, path: string = 'root'): string[] => {
  const paths: string[] = [];
  const stack: Array<{ v: unknown; p: string }> = [{ v: value, p: path }];
  let nodesCount = 0;

  while (stack.length > 0 && nodesCount < maxNodes) {
    const { v, p } = stack.pop()!;
    const type = getValueType(v);

    if (type === 'object' || type === 'array') {
      paths.push(p);
      nodesCount++;

      const entries = type === 'array'
        ? (v as unknown[]).map((child, i) => [i, child] as [number, unknown])
        : Object.entries(v as object);

      const limit = Math.min(entries.length, 100);
      for (let i = limit - 1; i >= 0; i--) {
        const [k, vChild] = entries[i];
        stack.push({ v: vChild, p: `${p}.${k}` });
      }
    }
  }

  return paths;
};

/**
 * Parse a user-edited string back to the appropriate JS value.
 * Recognizes null, booleans, numbers; everything else becomes a string.
 */
const parseEditedValue = (raw: string): unknown => {
  const trimmed = raw.trim();
  if (trimmed === 'null') return null;
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === '') return '';
  // Number (including negative, float, scientific)
  if (/^-?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(trimmed)) {
    const n = Number(trimmed);
    if (Number.isFinite(n)) return n;
  }
  return raw;
};

/**
 * Inline-editable value cell. Shows an input on click, commits on Enter/blur.
 */
const EditableValue: React.FC<{
  value: unknown;
  type: ValueType;
  path: string;
  searchTerm: string;
  onValueEdit: (path: string, newValue: unknown) => void;
}> = ({ value, type, path, searchTerm, onValueEdit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    const raw = value === null ? 'null' : type === 'string' ? String(value) : String(value);
    setDraft(raw);
    setEditing(true);
  };

  React.useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const parsed = parseEditedValue(draft);
    // Only emit if value actually changed
    if (parsed !== value) {
      onValueEdit(path, parsed);
    }
  };

  const cancel = () => {
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  if (editing) {
    const colorClass = TYPE_COLORS[type];
    return (
      <input
        ref={inputRef}
        className={cn(
          'bg-muted/50 border border-primary/50 rounded px-1 py-0 text-sm font-mono outline-none min-w-[60px]',
          colorClass
        )}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        style={{ width: `${Math.max(60, draft.length * 8 + 16)}px` }}
      />
    );
  }

  const colorClass = TYPE_COLORS[type];
  const displayValue = type === 'string' ? `"${value}"` : String(value);

  return (
    <span
      className={cn(colorClass, 'cursor-text hover:bg-muted/50 rounded px-0.5 transition-colors')}
      onClick={startEdit}
      title="Click to edit"
    >
      {highlightMatch(displayValue, searchTerm)}
    </span>
  );
};

const AddPropertyInline: React.FC<{
  onAdd: (key: string) => void;
  onCancel: () => void;
}> = ({ onAdd, onCancel }) => {
  const [draft, setDraft] = useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commit = () => {
    const key = draft.trim();
    if (key) onAdd(key);
    else onCancel();
  };

  return (
    <span className="inline-flex items-center gap-1">
      <input
        ref={inputRef}
        className="bg-muted/50 border border-primary/50 rounded px-1 py-0 text-xs font-mono outline-none w-24"
        placeholder="key name"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        onClick={(e) => e.stopPropagation()}
      />
    </span>
  );
};

const TreeNode: React.FC<TreeNodeProps & { searchResult: SearchResult }> = ({
  keyName,
  value,
  index,
  depth,
  maxDepth,
  path,
  expandedPaths,
  toggleExpand,
  searchTerm,
  searchMode,
  searchResult,
  showTypes,
  zeroIndexed,
  chunkSize,
  editMode,
  activeMatchPath,
  onValueEdit,
  onNodeAdd,
  onNodeDelete,
}) => {
  const [renderedCount, setRenderedCount] = useState(chunkSize);
  const type = getValueType(value);
  const isExpandable = type === 'object' || type === 'array';
  const hasChildren = isExpandable && Object.keys(value as object).length > 0;
  const isExpanded = expandedPaths.has(path);
  
  const [addingProperty, setAddingProperty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const descendantCount = useMemo(() => {
    if (!isExpandable) return 0;
    let count = 0;
    const stack: unknown[] = [value];
    while (stack.length > 0 && count < 1000) {
      const v = stack.pop()!;
      const t = getValueType(v);
      if (t === 'array') for (const c of v as unknown[]) { count++; stack.push(c); }
      else if (t === 'object') for (const c of Object.values(v as object)) { count++; stack.push(c); }
    }
    return count;
  }, [value, isExpandable]);

  const WARN_THRESHOLD = 10;
  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (descendantCount >= WARN_THRESHOLD && !confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setConfirmDelete(false);
    onNodeDelete?.(path);
  }, [descendantCount, confirmDelete, onNodeDelete, path]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      toggleExpand(path);
    }
  };

  const renderValue = () => {
    const colorClass = TYPE_COLORS[type];
    
    if (!isExpandable) {
      if (editMode && onValueEdit) {
        return (
          <EditableValue
            value={value}
            type={type}
            path={path}
            searchTerm={searchTerm}
            onValueEdit={onValueEdit}
          />
        );
      }
      const displayValue = type === 'string' ? `"${value}"` : String(value);
      return (
        <span className={colorClass}>
          {highlightMatch(displayValue, searchTerm)}
        </span>
      );
    }

    if (!isExpanded) {
      return (
        <span className={cn(colorClass, 'opacity-70')}>
          {getValuePreview(value, type)}
        </span>
      );
    }

    return null;
  };

  const renderChildren = () => {
    if (!isExpanded || depth >= maxDepth) return null;
    // Allow rendering the container even when empty (for add buttons)
    if (!isExpandable) return null;

    const allEntries = type === 'array'
      ? (value as unknown[]).map((v, i) => [i, v] as [number, unknown])
      : Object.entries(value as object);

    // Filter at the iteration site — non-matching children are never mounted
    const isFiltering = searchTerm && searchMode === 'filter';
    const entries = isFiltering
      ? allEntries.filter(([k]) => searchResult.matchingPaths.has(`${path}.${k}`))
      : allEntries;

    const total = entries.length;
    const items = entries.slice(0, renderedCount);

    return (
      <div className="ml-4 border-l border-border/50 pl-3">
        {items.map(([k, v], i) => (
          <TreeNode
            key={String(k)}
            keyName={k}
            value={v}
            index={i}
            depth={depth + 1}
            maxDepth={maxDepth}
            path={`${path}.${k}`}
            expandedPaths={expandedPaths}
            searchResult={searchResult}
            toggleExpand={toggleExpand}
            searchTerm={searchTerm}
            searchMode={searchMode}
            showTypes={showTypes}
            zeroIndexed={zeroIndexed}
            chunkSize={chunkSize}
            editMode={editMode}
            activeMatchPath={activeMatchPath}
            onValueEdit={onValueEdit}
            onNodeAdd={onNodeAdd}
            onNodeDelete={onNodeDelete}
          />
        ))}

        {renderedCount < total && (
          <button
            type="button"
            className="text-xs text-primary hover:underline mt-1"
            onClick={(e) => {
              e.stopPropagation();
              setRenderedCount(prev => prev + chunkSize);
            }}
          >
            Show {Math.min(chunkSize, total - renderedCount)} more... ({total - renderedCount} remaining)
          </button>
        )}

        {editMode && onNodeAdd && type === 'object' && (
          <div className="py-0.5">
            {addingProperty ? (
              <AddPropertyInline
                onAdd={(key) => { setAddingProperty(false); onNodeAdd(path, 'property', key); }}
                onCancel={() => setAddingProperty(false)}
              />
            ) : (
              <button
                type="button"
                className="text-xs text-primary/80 hover:text-primary hover:underline"
                onClick={(e) => { e.stopPropagation(); setAddingProperty(true); }}
              >
                + Add Property
              </button>
            )}
          </div>
        )}

        {editMode && onNodeAdd && type === 'array' && (
          <div className="py-0.5">
            <button
              type="button"
              className="text-xs text-primary/80 hover:text-primary hover:underline"
              onClick={(e) => { e.stopPropagation(); onNodeAdd(path, 'item'); }}
            >
              + Add Item
            </button>
          </div>
        )}
      </div>
    );
  };

  const keyDisplay= keyName !== null 
    ? (typeof keyName === 'string' ? keyName : String(keyName))
    : '';

  const isActiveMatch = path === activeMatchPath;

  return (
    <div className="json-tree-node" data-json-tree-path={path}>
      <div
        className={cn(
          'flex items-center gap-1 py-0.5 rounded hover:bg-muted/30 group',
          hasChildren && 'cursor-pointer',
          isActiveMatch && 'bg-yellow-500/20 ring-1 ring-yellow-500/50',
        )}
        onClick={handleToggle}
      >
        {/* Expand/collapse indicator */}
        <span className="w-4 h-4 flex items-center justify-center text-muted-foreground select-none flex-shrink-0">
          {hasChildren ? (
            <svg 
              className={cn('w-3 h-3 transition-transform', isExpanded && 'rotate-90')} 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <span className="w-3" />
          )}
        </span>
        
        {/* Key name */}
        {keyName !== null ? (
          <>
            {index !== undefined && (
              <span className="text-muted-foreground/70 text-[10px] mr-1 select-none font-mono">
                #{zeroIndexed ? index : index + 1}
              </span>
            )}
            <span className="text-cyan-400">
              {highlightMatch(keyDisplay, searchTerm)}
            </span>
            <span className="text-muted-foreground">:</span>
          </>
        ) : (
          index !== undefined && (
            <span className="text-muted-foreground/70 text-[10px] mr-1 select-none font-mono">
              #{zeroIndexed ? index : index + 1}
            </span>
          )
        )}
        
        {/* Type badge */}
        {showTypes && (
          <span className={cn('text-xs px-1 rounded opacity-60', TYPE_COLORS[type])}>
            {type === 'array' ? '[]' : type === 'object' ? '{}' : TYPE_LABELS[type]}
          </span>
        )}
        
        {/* Value or bracket */}
        {isExpandable && isExpanded ? (
          <span className={cn(TYPE_COLORS[type], 'opacity-70')}>
            {type === 'array' ? `Array[${(value as unknown[]).length}]` : `Object{${Object.keys(value as object).length}}`}
          </span>
        ) : (
          renderValue()
        )}
        
        {/* Copy button on hover */}
        <button
          className="ml-auto opacity-60 group-hover:opacity-100 text-xs text-muted-foreground hover:text-foreground px-1"
          type="button"
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            void copyToClipboard(formatValueForCopy(value));
          }}
          title="Copy value"
          aria-label="Copy value"
        >
          <ClipboardCopy className="h-2.5 w-2.5" />
        </button>

        {/* Delete button (edit mode, non-root only) */}
        {editMode && onNodeDelete && path !== 'root' && (
          <button
            className="opacity-60 group-hover:opacity-100 text-xs text-red-400 hover:text-red-300 px-1"
            type="button"
            onClick={handleDelete}
            title="Delete property"
            aria-label="Delete property"
          >
            <Trash2 className="h-2.5 w-2.5" />
          </button>
        )}
      </div>

      {/* Delete confirmation overlay */}
      {confirmDelete && (
        <div className="ml-5 my-1 p-2 rounded border border-red-500/30 bg-red-500/10 text-xs flex items-center gap-2">
          <span className="text-red-400">
            Delete &quot;{keyDisplay}&quot; and {descendantCount} children?
          </span>
          <button
            type="button"
            className="px-2 py-0.5 rounded bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30"
            onClick={handleDelete}
          >
            Delete
          </button>
          <button
            type="button"
            className="px-2 py-0.5 rounded bg-muted text-muted-foreground hover:bg-muted/80"
            onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
          >
            Cancel
          </button>
        </div>
      )}
      
      {/* Children */}
      {renderChildren()}
    </div>
  );
};

export const JsonTreeView: React.FC<JsonTreeViewProps> = ({
  data,
  initialExpanded = true,
  maxDepth = 10,
  showTypes: controlledShowTypes,
  onShowTypesChange,
  zeroIndexed = true,
  searchTerm: controlledSearchTerm,
  onSearchTermChange,
  searchMode: controlledSearchMode,
  onSearchModeChange,
  chunkSize = 100,
  maxTraversalNodes = 5000,
  editMode = false,
  onValueEdit,
  onNodeAdd,
  onNodeDelete,
  expandAllTrigger,
  collapseAllTrigger,
}) => {
  const treeScrollRef = React.useRef<HTMLDivElement>(null);
  const [uncontrolledSearchTerm, setUncontrolledSearchTerm] = useState('');
  const [uncontrolledShowTypes, setUncontrolledShowTypes] = useState<boolean>(controlledShowTypes ?? true);
  const [uncontrolledSearchMode, setUncontrolledSearchMode] = useState<JsonSearchMode>(controlledSearchMode ?? DEFAULT_SEARCH_MODE);
  
  // Local search state for immediate UI feedback (breaks synchronous render chain)
  const [localSearchTerm, setLocalSearchTerm] = useState(controlledSearchTerm ?? '');

  const searchTerm = controlledSearchTerm ?? uncontrolledSearchTerm;
  const showTypes = controlledShowTypes ?? uncontrolledShowTypes;
  const searchMode = controlledSearchMode ?? uncontrolledSearchMode;
  
  // Calculate all paths for expand all functionality
  const allPaths = useMemo(() => collectAllPaths(data, maxTraversalNodes), [data, maxTraversalNodes]);
  
  // Pre-calculate search results
  const searchResult = useMemo(() => {
    return performSearch(data, searchTerm, maxTraversalNodes);
  }, [data, searchTerm, maxTraversalNodes]);

  // Active match navigation (works in both modes)
  const [activeMatchIndex, setActiveMatchIndex] = useState(-1);
  const totalMatches = searchResult.matchingLeafPaths.length;
  const activeMatchPath = totalMatches > 0 && activeMatchIndex >= 0
    ? searchResult.matchingLeafPaths[activeMatchIndex]
    : null;

  // Reset active index when search term or results change
  useEffect(() => {
    setActiveMatchIndex(totalMatches > 0 ? 0 : -1);
  }, [searchTerm, totalMatches]);

  const goToNextMatch = useCallback(() => {
    setActiveMatchIndex(prev => (prev + 1) % totalMatches);
  }, [totalMatches]);

  const goToPrevMatch = useCallback(() => {
    setActiveMatchIndex(prev => (prev - 1 + totalMatches) % totalMatches);
  }, [totalMatches]);

  // Initial expanded paths state
  const [internalExpandedPaths, setInternalExpandedPaths] = useState<Set<string>>(() => {
    if (initialExpanded) {
      // Expand first 2 levels by default
      return new Set(allPaths.filter(p => p.split('.').length <= 3));
    }
    return new Set(['root']);
  });

  const expandedPaths = useMemo(() => {
    if (searchTerm && searchResult.expandedPaths.size > 0) {
      // Both modes auto-expand to show matching nodes
      if (searchMode === 'filter') {
        return searchResult.expandedPaths;
      }
      // Highlight mode: merge search expansions with user's manual state
      const merged = new Set(internalExpandedPaths);
      for (const p of searchResult.expandedPaths) merged.add(p);
      return merged;
    }
    return internalExpandedPaths;
  }, [searchTerm, searchMode, searchResult.expandedPaths, internalExpandedPaths]);

  const toggleExpand = useCallback((path: string) => {
    setInternalExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setInternalExpandedPaths(new Set(allPaths));
  }, [allPaths]);

  const collapseAll = useCallback(() => {
    setInternalExpandedPaths(new Set(['root']));
  }, []);

  // React to parent-driven expand/collapse triggers
  const prevExpandTrigger = useRef(expandAllTrigger);
  const prevCollapseTrigger = useRef(collapseAllTrigger);

  useEffect(() => {
    if (expandAllTrigger && expandAllTrigger !== prevExpandTrigger.current) {
      expandAll();
    }
    prevExpandTrigger.current = expandAllTrigger;
  }, [expandAllTrigger, expandAll]);

  useEffect(() => {
    if (collapseAllTrigger && collapseAllTrigger !== prevCollapseTrigger.current) {
      collapseAll();
    }
    prevCollapseTrigger.current = collapseAllTrigger;
  }, [collapseAllTrigger, collapseAll]);

  // Sync controlled prop → local state
  useEffect(() => {
    if (controlledSearchTerm !== undefined) {
      setLocalSearchTerm(controlledSearchTerm);
    }
  }, [controlledSearchTerm]);

  // Ref for callback to avoid effect re-fire on parent re-renders
  const onSearchTermChangeRef = useRef(onSearchTermChange);
  onSearchTermChangeRef.current = onSearchTermChange;

  // Debounced propagation to parent (refs break the synchronous render chain)
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchTermChangeRef.current?.(localSearchTerm);
      if (controlledSearchTerm === undefined) {
        setUncontrolledSearchTerm(localSearchTerm);
      }
    }, 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes onSearchTermChangeRef (stable ref pattern)
  }, [localSearchTerm, controlledSearchTerm]);

  // Handler for immediate local state update
  const handleSearch = useCallback((term: string) => {
    setLocalSearchTerm(term);
  }, []);

  const handleShowTypesChange = useCallback((next: boolean) => {
    onShowTypesChange?.(next);
    if (controlledShowTypes === undefined) {
      setUncontrolledShowTypes(next);
    }
  }, [controlledShowTypes, onShowTypesChange]);

  const handleSearchModeChange = useCallback((next: JsonSearchMode) => {
    onSearchModeChange?.(next);
    if (controlledSearchMode === undefined) {
      setUncontrolledSearchMode(next);
    }
  }, [controlledSearchMode, onSearchModeChange]);

  useEffect(() => {
    const el = treeScrollRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      if (!activeMatchPath) {
        if (!searchTerm.trim()) el.scrollTop = 0;
        return;
      }

      const activeEl = el.querySelector(`[data-json-tree-path="${CSS.escape(activeMatchPath)}"]`) as HTMLElement | null;
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  }, [activeMatchPath, searchTerm]);

  const [showLegend, setShowLegend] = useState(false);

  return (
    <div className="json-tree h-full flex flex-col bg-background">
      {/* Compact toolbar: search + actions + settings */}
      <div className="flex items-center gap-2 px-3 h-8 border-b border-border bg-muted/30 shrink-0">
        <SearchBar
          compact
          query={localSearchTerm}
          onQueryChange={handleSearch}
          placeholder="Search..."
          resultCount={totalMatches}
          currentIndex={activeMatchIndex}
          onNext={goToNextMatch}
          onPrev={goToPrevMatch}
        />

        <div className="flex-1" />

        {/* Expand/Collapse always visible */}
        <button
          type="button"
          className="h-6 w-6 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted shrink-0"
          title="Expand all"
          aria-label="Expand all"
          onClick={expandAll}
        >
          <ChevronsDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="h-6 w-6 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted shrink-0"
          title="Collapse all"
          aria-label="Collapse all"
          onClick={collapseAll}
        >
          <ChevronsUp className="h-3.5 w-3.5" />
        </button>

        {/* Search mode toggle */}
        <button
          type="button"
          className={cn(
            'h-6 px-1.5 inline-flex items-center justify-center rounded-md text-[10px] font-medium shrink-0',
            searchMode === 'filter'
              ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20'
              : 'hover:bg-muted text-muted-foreground'
          )}
          title={searchMode === 'filter' ? 'Search mode: Filter (click for Highlight)' : 'Search mode: Highlight (click for Filter)'}
          aria-label="Toggle search mode"
          onClick={() => handleSearchModeChange(searchMode === 'filter' ? 'highlight' : 'filter')}
        >
          {searchMode === 'filter' ? 'Filter' : 'Highlight'}
        </button>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="h-6 w-6 inline-flex items-center justify-center rounded-md hover:bg-muted shrink-0"
              title="Settings"
              aria-label="Settings"
            >
              <Settings className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="p-0 w-72">
            <div className="px-3 py-2 flex items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground">Show Types</div>
              <Switch checked={showTypes} onCheckedChange={handleShowTypesChange} />
            </div>

            <div className="px-3 py-2 flex items-center justify-between gap-3 border-t border-border">
              <div className="text-xs text-muted-foreground">Show Legend</div>
              <Switch checked={showLegend} onCheckedChange={setShowLegend} />
            </div>
          </PopoverContent>
        </Popover>
      </div>
      
      {/* Tree content */}
      <div ref={treeScrollRef} className="flex-1 overflow-auto p-3 font-mono text-sm">
        <TreeNode
          keyName={null}
          value={data}
          depth={0}
          maxDepth={maxDepth}
          path="root"
          expandedPaths={expandedPaths}
          searchResult={searchResult}
          toggleExpand={toggleExpand}
          searchTerm={searchTerm}
          searchMode={searchMode}
          showTypes={showTypes}
          zeroIndexed={zeroIndexed}
          chunkSize={chunkSize}
          editMode={editMode}
          activeMatchPath={activeMatchPath}
          onValueEdit={onValueEdit}
          onNodeAdd={onNodeAdd}
          onNodeDelete={onNodeDelete}
        />
      </div>

      {showLegend && (
        <div className="px-3 py-1.5 border-t border-border bg-muted/20 text-xs shrink-0">
          <div className="flex flex-wrap gap-4">
            <span className={TYPE_COLORS.string}>● Strings</span>
            <span className={TYPE_COLORS.number}>● Numbers</span>
            <span className={TYPE_COLORS.boolean}>● Booleans</span>
            <span className={TYPE_COLORS.object}>● Objects</span>
            <span className={TYPE_COLORS.array}>● Arrays</span>
            <span className={TYPE_COLORS.null}>● Null</span>
          </div>
        </div>
      )}
    </div>
  );
};

